/*
 * KÖRVONAL (2026-10-28): the vendor version moved the strip with a
 * requestAnimationFrame loop that wrote an inline transform on every child,
 * every frame, for as long as the strip was on screen. On PageSpeed's mobile
 * profile that loop cost 3.7 s of main-thread time on a PDP, the largest JS
 * cost on the page, while LCP was still pending. This version measures and
 * clones exactly as before, then hands the motion to a CSS keyframe animation
 * (section-marquee.css, .scrolling-text--css), which the compositor runs off the
 * main thread. Same speed (px/s, divided by 1.5 under 768 px), same direction,
 * pause on hover and off-screen. Re-apply after a theme upgrade.
 */
if (typeof ScrollingText !== "function") {
  class ScrollingText extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this.removeEventListener("mouseover", this._onMouseOver);
      this.removeEventListener("mouseout", this._onMouseOut);
    }

    init() {
      this._speed = window.innerWidth > 768
        ? parseFloat(this.dataset.scrollingSpeed)
        : parseFloat(this.dataset.scrollingSpeed) / 1.5;

      this._direction = this.dataset.scrollingDirection || "ltr";
      this._pauseOnHover = this.dataset.pauseOnHover === "true";

      this._onMouseOver = this._onMouseOver.bind(this);
      this._onMouseOut = this._onMouseOut.bind(this);
      this._onResize = this._onResize.bind(this);

      this._base = this.children[0];
      if (!this._base) return;

      this._base.style.position = "relative";
      this._template = this._base.cloneNode(true);

      this.#readSizes();
      this.#setupChildren();
      this.#applyAnimation();
      this.classList.add("scrolling-text--css");
      if (this._direction === "rtl") this.classList.add("scrolling-text--rtl");

      if (this._pauseOnHover) {
        this.addEventListener("mouseover", this._onMouseOver);
        this.addEventListener("mouseout", this._onMouseOut);
      }
      this._intersectionObserver = new IntersectionObserver(entries => {
        this.classList.toggle("scrolling-text--offscreen", !entries[0].isIntersecting);
      });
      this._intersectionObserver.observe(this);
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this);
    }

    #readSizes() {
      this._boxWidth = this.getBoundingClientRect().width;
      this._itemWidth = this._base.getBoundingClientRect().width;
    }

    #neededChildren() {
      return Math.ceil(this._boxWidth / this._itemWidth) + 1;
    }

    #setupChildren() {
      const needed = this.#neededChildren();
      const current = this.children.length;

      if (needed > current) {
        for (let i = current; i < needed; i++) {
          this.appendChild(this._template.cloneNode(true));
        }
      } else if (needed < current) {
        for (let i = current; i > needed; i--) {
          this.removeChild(this.lastElementChild);
        }
      }
    }

    #applyAnimation() {
      if (!this._itemWidth || !this._speed) return;
      this.style.setProperty("--scrolling-distance", `${this._itemWidth}px`);
      this.style.setProperty("--scrolling-duration", `${this._itemWidth / this._speed}s`);
    }

    // Children added after the animation started would run out of phase with
    // the rest, so a size change restarts all of them together.
    #restart() {
      for (const child of this.children) child.style.animation = "none";
      void this.offsetWidth;
      for (const child of this.children) child.style.animation = "";
    }

    _onMouseOver() {
      this.classList.add("scrolling-text--paused");
    }

    _onMouseOut() {
      this.classList.remove("scrolling-text--paused");
    }

    _onResize() {
      const box = this._boxWidth, item = this._itemWidth, count = this.children.length;
      this.#readSizes();
      if (box === this._boxWidth && item === this._itemWidth) return;
      this.#setupChildren();
      this.#applyAnimation();
      if (item !== this._itemWidth || count !== this.children.length) this.#restart();
    }

  }

  if ( typeof customElements.get("scrolling-text") == 'undefined' ) {
    customElements.define("scrolling-text", ScrollingText);
  }
}
